'use client';

import * as React from 'react';
import { useFormContext } from 'react-hook-form';

import { Textarea } from '@ui/components/ui/textarea';

import { cn } from '../../../lib/utils';
import { FormControl, FormItem, FormLabel, FormMessage } from '../form/Form';

interface FloatingTextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  name: string;
  label: string;
  required?: boolean;
  containerClassName?: string;
  textareaClassName?: string;
  placeholder?: string;
}

export const FloatingTextArea = ({
  ref,
  name,
  label,
  required,
  containerClassName,
  textareaClassName,
  placeholder,
  onFocus: providedOnFocus,
  onBlur: providedOnBlur,
  ...rest
}: FloatingTextAreaProps & { ref?: React.RefObject<HTMLTextAreaElement | null> }) => {
  const {
    register,
    watch,
    formState: { errors = {} }
  } = useFormContext();
  const [focused, setFocused] = React.useState(false);
  const hasValue = Boolean(watch(name));

  const rhfProps = register(name, { required });

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setFocused(true);
    providedOnFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setFocused(false);
    rhfProps.onBlur(e);
    providedOnBlur?.(e);
  };

  return (
    <FormItem className={cn('relative', containerClassName)}>
      <FormControl>
        <Textarea
          id={name}
          placeholder={placeholder}
          className={cn(
            'block w-full rounded-lg border p-3 transition-colors placeholder:text-white focus:placeholder:text-neutral-400 min-h-[100px] resize-none font-normal text-base md:text-sm',
            errors[name] && 'border-destructive focus:border-destructive',
            textareaClassName
          )}
          {...rest}
          {...rhfProps}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </FormControl>

      <FormLabel
        htmlFor={name}
        className={cn(
          'pointer-events-none absolute left-4 transition-all bg-background px-1 font-normal',
          focused || hasValue
            ? 'top-0 -translate-y-1/2 text-xs text-foreground'
            : 'top-4 text-base text-muted-foreground',
          focused && 'text-foreground'
        )}
      >
        {label}
        {required && <span className="text-destructive">*</span>}
      </FormLabel>

      <FormMessage />
    </FormItem>
  );
};

FloatingTextArea.displayName = 'FloatingTextArea';
